@if (isLoading()) {
  <div class="loading-container">
    <div class="spinner"></div>
    <p>{{ "loading" | translate }}</p>
  </div>
}
@if (!isLoading()) {
  <div class="dashboard-container">
    <!-- ── Page Header ────────────────────────────────────────────────────── -->
    <div class="dashboard-header">
      <div class="dashboard-header-info">
        <h2 class="dashboard-title">{{ "dashboardTitle" | translate }}</h2>
        <p class="dashboard-subtitle">{{ "dashboardSubtitle" | translate }}</p>
      </div>
      <div class="dashboard-header-actions">
        <button
          type="button"
          class="e-btn e-outline e-primary"
          (click)="navigateToEmailQueue()"
        >
          <span class="ti ti-mail"></span>
          {{ "allEmails" | translate }}
        </button>
        <button
          type="button"
          class="e-btn e-outline e-primary"
          (click)="navigateToSmsQueue()"
        >
          <span class="ti ti-message"></span>
          {{ "allSms" | translate }}
        </button>
      </div>
    </div>

    <!-- ── Stats Cards ────────────────────────────────────────────────────── -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-icon email">
          <i class="ti ti-mail"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats().emailPending }}</h3>
          <p>{{ "emailPending" | translate }}</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon sms">
          <i class="ti ti-message"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats().smsPending }}</h3>
          <p>{{ "smsPending" | translate }}</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon error">
          <i class="ti ti-alert-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats().failedToday }}</h3>
          <p>{{ "failedToday" | translate }}</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon success">
          <i class="ti ti-circle-check"></i>
        </div>
        <div class="stat-content">
          <h3>{{ stats().deliveredToday }}</h3>
          <p>{{ "deliveredToday" | translate }}</p>
        </div>
      </div>
    </div>

    <!-- ── Main Panels ─────────────────────────────────────────────────────── -->
    <div class="panels-grid">
      <!-- Recent Emails Panel -->
      <div class="panel">
        <div class="panel-header">
          <div class="panel-header-left">
            <div class="panel-icon-wrap">
              <i class="ti ti-mail"></i>
            </div>
            <h3 class="panel-title">{{ "recentEmails" | translate }}</h3>
          </div>
          <button
            type="button"
            class="e-btn e-flat e-small e-primary view-all-btn"
            (click)="navigateToEmailQueue()"
          >
            {{ "viewAll" | translate }}
            <span class="ti ti-chevron-right"></span>
          </button>
        </div>

        <div class="panel-body">
          @if (recentEmails().length === 0) {
            <div class="empty-state">
              <div class="empty-icon">
                <i class="ti ti-inbox"></i>
              </div>
              <p class="empty-title">{{ "noRecentEmails" | translate }}</p>
            </div>
          } @else {
            <div class="data-list">
              @for (email of recentEmails(); track email.id) {
                <div class="data-row">
                  <div class="data-row-main">
                    <span
                      class="status-badge"
                      [style.background-color]="
                        getEmailStatusColor(email.status)
                      "
                      [style.color]="getEmailStatusFgColor(email.status)"
                    >
                      {{ getEmailStatusName(email.status) }}
                    </span>
                    <div class="data-row-info">
                      <span class="row-name">{{ email.toEmail }}</span>
                      @if (email.subject) {
                        <span class="row-meta">{{ email.subject }}</span>
                      }
                    </div>
                  </div>
                  <div class="data-row-time">
                    <span class="row-time">{{
                      email.createdAt | dateTz: "short"
                    }}</span>
                  </div>
                  <button
                    type="button"
                    class="e-btn e-flat e-primary row-action-btn"
                    [title]="'viewDetail' | translate"
                    (click)="openEmailDetail(email.id)"
                  >
                    <span class="ti ti-eye icon-md"></span>
                  </button>
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Recent SMS Panel -->
      <div class="panel">
        <div class="panel-header">
          <div class="panel-header-left">
            <div class="panel-icon-wrap">
              <i class="ti ti-message"></i>
            </div>
            <h3 class="panel-title">{{ "recentSms" | translate }}</h3>
          </div>
          <button
            type="button"
            class="e-btn e-flat e-small e-primary view-all-btn"
            (click)="navigateToSmsQueue()"
          >
            {{ "viewAll" | translate }}
            <span class="ti ti-chevron-right"></span>
          </button>
        </div>

        <div class="panel-body">
          @if (recentSms().length === 0) {
            <div class="empty-state">
              <div class="empty-icon">
                <i class="ti ti-inbox"></i>
              </div>
              <p class="empty-title">{{ "noRecentSms" | translate }}</p>
            </div>
          } @else {
            <div class="data-list">
              @for (sms of recentSms(); track sms.id) {
                <div class="data-row">
                  <div class="data-row-main">
                    <span
                      class="status-badge"
                      [style.background-color]="getSmsStatusColor(sms.status)"
                      [style.color]="getSmsStatusFgColor(sms.status)"
                    >
                      {{ getSmsStatusName(sms.status) }}
                    </span>
                    <div class="data-row-info">
                      <span class="row-name">{{ sms.recipientPhone }}</span>
                      @if (sms.messageContent) {
                        <span class="row-meta"
                          >{{ sms.messageContent | slice: 0 : 60
                          }}{{
                            sms.messageContent.length > 60 ? "..." : ""
                          }}</span
                        >
                      }
                    </div>
                  </div>
                  <div class="data-row-time">
                    <span class="row-time">{{
                      sms.createdAt | dateTz: "short"
                    }}</span>
                  </div>
                  <button
                    type="button"
                    class="e-btn e-flat e-primary row-action-btn"
                    [title]="'viewDetail' | translate"
                    (click)="openSmsDetail(sms.id)"
                  >
                    <span class="ti ti-eye icon-md"></span>
                  </button>
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
